import { PaymentWorkflowType } from '../../types/api/graph.types';

const GIFT_REGISTER_TYPE = 'gift';
const HOSPITALITY_REGISTER_TYPE = 'hospitalityEntertainment';
const CHARITABLE_REGISTER_TYPE = 'charitableDonation';
const GIFT_AUTHORISER_MINIMUM = 100;
const HOSPITALITY_AUTHORISER_MINIMUM = 200;

export const GIFT_HOSPITALITY_APPROVAL_SECTION_KEYS = [
  'gift_hospitality_donation_uk_approval',
  'gift_hospitality_donation_dubai_approval',
  'gift_hospitality_donation_dublin_approval',
] as const;

/** Keep the approval schema wired, and leave the step out of the form until this is true. */
export const GIFT_HOSPITALITY_APPROVAL_STEP_ENABLED = false;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const unwrapValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.length > 0 ? unwrapValue(value[0]) : undefined;
  }

  if (isRecord(value) && 'value' in value) {
    return unwrapValue(value.value);
  }

  return value;
};

const toAmount = (value: unknown): number | null => {
  const unwrapped = unwrapValue(value);

  if (typeof unwrapped === 'number' && Number.isFinite(unwrapped)) {
    return unwrapped;
  }

  if (typeof unwrapped === 'string') {
    const normalised = unwrapped.replace(/[£,\s]/g, '');
    if (normalised === '') {
      return null;
    }

    const parsed = Number(normalised);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const toRegisterType = (value: unknown): string => {
  const unwrapped = unwrapValue(value);
  if (typeof unwrapped !== 'string') {
    return '';
  }

  const normalised = unwrapped.trim().toLowerCase().replace(/[\s/]/g, '');
  if (normalised === 'gift') {
    return GIFT_REGISTER_TYPE;
  }

  if (normalised === 'hospitalityentertainment' || normalised === 'hospitality') {
    return HOSPITALITY_REGISTER_TYPE;
  }

  if (normalised === 'charitabledonation') {
    return CHARITABLE_REGISTER_TYPE;
  }

  return '';
};

const hasAuthoriserValue = (value: unknown): boolean => {
  const unwrapped = unwrapValue(value);
  if (typeof unwrapped === 'string') {
    return unwrapped.trim() !== '';
  }

  if (isRecord(unwrapped)) {
    const mail = unwrapped.mail ?? unwrapped.value ?? unwrapped.label;
    return typeof mail === 'string' && mail.trim() !== '';
  }

  return false;
};

const readSectionKey = (section: unknown): string => {
  if (typeof section === 'string') {
    return section;
  }

  if (!isRecord(section)) {
    return '';
  }

  const key =
    section.sectionKey ?? section.SectionKey ?? section.key ?? section.name ?? section.title;

  return typeof key === 'string' ? key : '';
};

const readFieldsBag = (section: unknown): Record<string, unknown> => {
  if (!isRecord(section)) {
    return {};
  }

  const fields = section.fields ?? section.Fields;
  if (isRecord(fields)) {
    return fields;
  }

  if (!Array.isArray(fields)) {
    return {};
  }

  return fields.reduce<Record<string, unknown>>((values, field) => {
    if (!isRecord(field)) {
      return values;
    }

    const name = field.name ?? field.key ?? field.fieldName;
    if (typeof name === 'string') {
      values[name] = field.value;
    }

    return values;
  }, {});
};

const readNamedField = (
  fields: Record<string, unknown>,
  name: string
): unknown => {
  if (name in fields) {
    return fields[name];
  }

  const pascalName = name.charAt(0).toUpperCase() + name.slice(1);
  return pascalName in fields ? fields[pascalName] : undefined;
};

const isRequestDetailsSection = (section: unknown): boolean => {
  const key = readSectionKey(section);
  return key.endsWith('_request_details') || key === 'Request Details';
};

const sectionHasRequestValues = (section: unknown): boolean => {
  const fields = readFieldsBag(section);
  return (
    readNamedField(fields, 'registerType') !== undefined ||
    readNamedField(fields, 'estimatedValue') !== undefined ||
    readNamedField(fields, 'authoriser') !== undefined
  );
};

const pickUsable = (
  topLevel: unknown,
  saved: unknown,
  isUsable: (value: unknown) => boolean
): unknown => (isUsable(topLevel) ? topLevel : saved);

const readAuthoriserFields = (
  source: Record<string, unknown>
): Record<string, unknown> => {
  const sections = Array.isArray(source.sections) ? source.sections : [];
  const requestDetails =
    sections.find(isRequestDetailsSection) ??
    sections.find(sectionHasRequestValues);
  const sectionFields = readFieldsBag(requestDetails);

  return {
    registerType: pickUsable(
      source.registerType,
      readNamedField(sectionFields, 'registerType'),
      (value) => toRegisterType(value) !== ''
    ),
    estimatedValue: pickUsable(
      source.estimatedValue,
      readNamedField(sectionFields, 'estimatedValue'),
      (value) => toAmount(value) !== null
    ),
    authoriser: pickUsable(
      source.authoriser,
      readNamedField(sectionFields, 'authoriser'),
      hasAuthoriserValue
    ),
  };
};

/**
 * Gift at or above £100, or hospitality at or above £200, needs an authoriser.
 * Saved request-details fields are used when the live form value is empty.
 */
export const requiresGiftHospitalityAuthoriser = (
  source: unknown
): boolean => {
  if (!isRecord(source)) {
    return false;
  }

  const fields = readAuthoriserFields(source);
  const registerType = toRegisterType(fields.registerType);
  const amount = toAmount(fields.estimatedValue);

  if (registerType === GIFT_REGISTER_TYPE && amount !== null) {
    return amount >= GIFT_AUTHORISER_MINIMUM;
  }

  if (registerType === HOSPITALITY_REGISTER_TYPE && amount !== null) {
    return amount >= HOSPITALITY_AUTHORISER_MINIMUM;
  }

  if (registerType === CHARITABLE_REGISTER_TYPE) {
    return false;
  }

  return hasAuthoriserValue(fields.authoriser);
};

/**
 * Approval stays hidden while `GIFT_HOSPITALITY_APPROVAL_STEP_ENABLED` is false.
 * When that flag is on, the step follows the authoriser value rule.
 */
export const shouldShowGiftHospitalityApproval = (source: unknown): boolean =>
  GIFT_HOSPITALITY_APPROVAL_STEP_ENABLED &&
  requiresGiftHospitalityAuthoriser(source);

export const isGiftHospitalityWorkflow = (
  workflowType: PaymentWorkflowType | undefined
): boolean =>
  workflowType === PaymentWorkflowType.GiftHospitalityDonationUk ||
  workflowType === PaymentWorkflowType.GiftHospitalityDonationDubai ||
  workflowType === PaymentWorkflowType.GiftHospitalityDonationDublin;

export const getGiftHospitalitySectionFlow = (
  sectionKeys: readonly string[],
  source: unknown
): string[] => {
  const approvalSectionKey = sectionKeys.find((sectionKey) =>
    (GIFT_HOSPITALITY_APPROVAL_SECTION_KEYS as readonly string[]).includes(
      sectionKey
    )
  );

  if (!approvalSectionKey || shouldShowGiftHospitalityApproval(source)) {
    return [...sectionKeys];
  }

  return sectionKeys.filter((sectionKey) => sectionKey !== approvalSectionKey);
};
